import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';

interface FeatureFlags {
  customerIntentEnabled: boolean;
  voiceAssistantEnabled: boolean;
  chatbotEnabled: boolean;
  analyticsEnabled: boolean;
  screenshotEnabled: boolean;
}

interface FeatureTogglesProps {
  siteId: string;
}

export function FeatureToggles({ siteId }: FeatureTogglesProps) {
  const [features, setFeatures] = useState<FeatureFlags>({
    customerIntentEnabled: false,
    voiceAssistantEnabled: true,
    chatbotEnabled: true,
    analyticsEnabled: true,
    screenshotEnabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadFeatures();
  }, [siteId]);

  const loadFeatures = async () => {
    try {
      const response = await authFetch(`/sites/${siteId}/features`);
      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      }
    } catch (error) {
      console.error('Error loading features:', error);
      toast({
        title: 'Error',
        description: 'Failed to load feature settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFeature = async (feature: keyof FeatureFlags, enabled: boolean) => {
    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/features`, {
        method: 'PUT',
        body: JSON.stringify({ [feature]: enabled }),
      });

      if (response.ok) {
        setFeatures(prev => ({ ...prev, [feature]: enabled }));
        toast({
          title: 'Success',
          description: 'Feature settings updated',
        });
      } else {
        throw new Error('Failed to update feature');
      }
    } catch (error) {
      console.error('Error updating feature:', error);
      toast({
        title: 'Error',
        description: 'Failed to update feature settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading features...</div>;
  }

  const featureDescriptions = {
    customerIntentEnabled: {
      title: 'Customer Intent Analysis',
      description: 'Analyze customer behavior and intents using AI. Disabling saves costs.',
      experimental: true,
    },
    voiceAssistantEnabled: {
      title: 'Voice Assistant',
      description: 'Enable voice-based interactions with the chatbot',
    },
    chatbotEnabled: {
      title: 'Chatbot',
      description: 'Enable the AI-powered chatbot on your site',
    },
    analyticsEnabled: {
      title: 'Analytics & Tracking',
      description: 'Track user behavior and page interactions',
    },
    screenshotEnabled: {
      title: 'Visual Screenshots',
      description: 'Capture visual states for better context understanding',
    },
    naturalLanguageSearchEnabled: {
      title: 'Natural Language Search',
      description: 'Enable AI-powered natural language search across your site content',
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature Settings</CardTitle>
        <CardDescription>
          Enable or disable specific features for this site
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(features).map(([key, enabled]) => {
          // Skip non-feature fields
          if (key === 'config' || key === 'voiceMode' || key === 'voicePushToTalkEnabled' || key === 'voiceRealtimeEnabled') {
            return null;
          }

          const feature = key as keyof FeatureFlags;
          const info = featureDescriptions[feature];

          // Skip if no description available
          if (!info) {
            console.warn(`No description for feature: ${key}`);
            return null;
          }
          
          return (
            <div key={key} className="flex items-center justify-between space-x-4">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor={key} className="text-base font-medium">
                    {info.title}
                  </Label>
                  {info.experimental && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      Experimental
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {info.description}
                </p>
              </div>
              <Switch
                id={key}
                checked={enabled}
                onCheckedChange={(checked) => updateFeature(feature, checked)}
                disabled={saving}
              />
            </div>
          );
        })}
        
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium">Cost Optimization Tip</p>
              <p>
                Customer Intent Analysis is currently disabled by default to reduce LLM costs. 
                Enable it only for sites where you need detailed behavioral analytics.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}